// Web payload encryption, protocol v1 (VAPT "Credentials Transmitted to Server in Plain Text").
// Source of truth: docs/security/web-payload-encryption.md in the backend repository.
// This file holds only the protocol (no network, no app imports) so it can be tested and
// cross-checked against the backend decryptor in isolation.

export const ENC_VERSION = 1;
export const KEY_WRAP_ALG = "RSA-OAEP-256";
export const CONTENT_ENC = "A256GCM";
const AAD_PREFIX = "inkaa-web-enc";
const KID_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const MIN_RSA_BITS = 2048;
const AES_KEY_BITS = 256;
const GCM_IV_BYTES = 12;
const GCM_TAG_BITS = 128;

export type WebEncryptionFailure =
  | "UNSUPPORTED_BROWSER"
  | "KEY_UNAVAILABLE"
  | "INVALID_KEY_RESPONSE"
  | "ENCRYPTION_FAILED"
  | "PAYLOAD_TOO_LARGE"
  | "ROUTE_NOT_ALLOWED";

/**
 * Raised before anything is sent. The message is deliberately generic: it is shown to
 * users and must never contain payloads, keys or cryptographic detail.
 */
export class WebEncryptionError extends Error {
  readonly isWebEncryptionError = true;
  readonly failure: WebEncryptionFailure;

  constructor(failure: WebEncryptionFailure) {
    super("Secure connection could not be established. Please try again later.");
    this.name = "WebEncryptionError";
    this.failure = failure;
  }
}

export interface PublicKeyInfo {
  v: number;
  kid: string;
  alg: string;
  enc: string;
  publicKey: string;
  keySize: number;
  serverTime: number;
  maxClockSkewSeconds: number;
  maxEnvelopeBytes: number;
  supportedVersions: number[];
}

export interface ServerKey {
  kid: string;
  cryptoKey: CryptoKey;
  /** serverTime - local time when the key was fetched, used to correct a skewed clock. */
  clockOffsetMs: number;
  maxEnvelopeBytes: number;
}

export interface Envelope {
  v: number;
  kid: string;
  ts: number;
  ek: string;
  iv: string;
  ct: string;
}

export function getSubtle(): SubtleCrypto {
  const webCrypto: Crypto | undefined = (globalThis as any).crypto;
  // crypto.subtle only exists in secure contexts (HTTPS or localhost).
  if (!webCrypto || !webCrypto.subtle || typeof webCrypto.getRandomValues !== "function") {
    throw new WebEncryptionError("UNSUPPORTED_BROWSER");
  }
  return webCrypto.subtle;
}

/** Standard base64 (RFC 4648 §4, padded), as required by the backend. */
export function base64Encode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function base64Decode(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

const isInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value);

/** Validates the documented GET /public/web/encryption/public-key response. */
export function parsePublicKeyInfo(body: unknown): PublicKeyInfo {
  const info = body as PublicKeyInfo;
  const valid =
    !!info &&
    typeof info === "object" &&
    info.v === ENC_VERSION &&
    Array.isArray(info.supportedVersions) &&
    info.supportedVersions.includes(ENC_VERSION) &&
    info.alg === KEY_WRAP_ALG &&
    info.enc === CONTENT_ENC &&
    typeof info.kid === "string" &&
    KID_PATTERN.test(info.kid) &&
    typeof info.publicKey === "string" &&
    info.publicKey.length > 0 &&
    isInteger(info.keySize) &&
    info.keySize >= MIN_RSA_BITS &&
    isInteger(info.serverTime) &&
    info.serverTime > 0 &&
    isInteger(info.maxClockSkewSeconds) &&
    info.maxClockSkewSeconds > 0 &&
    isInteger(info.maxEnvelopeBytes) &&
    info.maxEnvelopeBytes > 0;
  if (!valid) {
    throw new WebEncryptionError("INVALID_KEY_RESPONSE");
  }
  return info;
}

export async function importServerKey(
  info: PublicKeyInfo,
  now: () => number = Date.now
): Promise<ServerKey> {
  const subtle = getSubtle();
  let cryptoKey: CryptoKey;
  try {
    cryptoKey = await subtle.importKey(
      "spki",
      base64Decode(info.publicKey),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );
  } catch {
    throw new WebEncryptionError("INVALID_KEY_RESPONSE");
  }
  const modulusLength = (cryptoKey.algorithm as RsaHashedKeyAlgorithm).modulusLength;
  if (modulusLength !== info.keySize) {
    throw new WebEncryptionError("INVALID_KEY_RESPONSE");
  }
  return {
    kid: info.kid,
    cryptoKey,
    clockOffsetMs: info.serverTime - now(),
    maxEnvelopeBytes: info.maxEnvelopeBytes,
  };
}

/** AAD: inkaa-web-enc|v1|<kid>|<ts>|<METHOD>|<route>, UTF-8. */
export function additionalData(kid: string, ts: number, method: string, route: string) {
  return [AAD_PREFIX, "v" + ENC_VERSION, kid, String(ts), method.toUpperCase(), route].join("|");
}

/** Route as bound into the AAD: backend path only, no host/base path, query or trailing slash. */
export function isValidRoute(route: string) {
  return (
    route.startsWith("/") &&
    !route.includes("?") &&
    !route.includes("#") &&
    !route.includes("//") &&
    (route.length === 1 || !route.endsWith("/"))
  );
}

/**
 * Encrypts `payload` (the exact JSON the legacy route accepts) for `method route`.
 * A fresh AES-256 key and 96-bit nonce are generated for every call.
 */
export async function encryptEnvelope(
  key: ServerKey,
  method: string,
  route: string,
  payload: unknown,
  now: () => number = Date.now
): Promise<Envelope> {
  if (!isValidRoute(route)) {
    throw new WebEncryptionError("ROUTE_NOT_ALLOWED");
  }
  const subtle = getSubtle();
  const encoder = new TextEncoder();
  const ts = Math.round(now() + key.clockOffsetMs);
  let envelope: Envelope;
  try {
    const aad = encoder.encode(additionalData(key.kid, ts, method, route));
    const aesKey = await subtle.generateKey({ name: "AES-GCM", length: AES_KEY_BITS }, true, [
      "encrypt",
    ]);
    const iv = (globalThis as any).crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
    const ct = new Uint8Array(
      await subtle.encrypt(
        { name: "AES-GCM", iv, additionalData: aad, tagLength: GCM_TAG_BITS },
        aesKey,
        encoder.encode(JSON.stringify(payload === undefined ? {} : payload))
      )
    );
    const rawKey = new Uint8Array(await subtle.exportKey("raw", aesKey));
    const ek = new Uint8Array(await subtle.encrypt({ name: "RSA-OAEP" }, key.cryptoKey, rawKey));
    rawKey.fill(0);
    envelope = {
      v: ENC_VERSION,
      kid: key.kid,
      ts,
      ek: base64Encode(ek),
      iv: base64Encode(iv),
      ct: base64Encode(ct),
    };
  } catch {
    throw new WebEncryptionError("ENCRYPTION_FAILED");
  }
  if (encoder.encode(JSON.stringify(envelope)).length > key.maxEnvelopeBytes) {
    throw new WebEncryptionError("PAYLOAD_TOO_LARGE");
  }
  return envelope;
}
